// MVP API 의 작품·회차 Connect-RPC 클라이언트 (proto/serial/v1/serial.proto, DEC-101).
// 생성 타입(src/gen)을 도메인 타입으로 바꿔 넘긴다 — 화면은 생성 타입을 모른다. 로그인 토큰은 넘겨받은 전송(rpcTransport)이 싣는다
import { timestampDate, type Timestamp } from '@bufbuild/protobuf/wkt';
import { Code, ConnectError, createClient, type Transport } from '@connectrpc/connect';

import {
  AiUsage as AiUsagePB,
  SerialService,
  type Episode as EpisodePB,
  type EpisodeSummary as EpisodeSummaryPB,
  type Work as WorkPB,
} from '../../../gen/serial/v1/serial_pb';
import type { AiUsage, Episode, EpisodeSummary, Work, WorkDetail, WorksPage } from '../domain/serial';

/** 서버가 거절했거나 닿지 못했다. code 는 화면 문구 키를 고르는 이름(not_author 등), 닿지 못했으면 'network' */
export class SerialApiError extends Error {
  constructor(
    readonly code: string,
    /** 다시 불러도 결과가 같은 거절(없음·권한·형식·상한)이면 false — 재시도하지 않는다 */
    readonly retryable: boolean,
    readonly detail: Readonly<Record<string, unknown>> = {},
  ) {
    super(`serial rpc ${code}`);
    this.name = 'SerialApiError';
  }
}

const NOT_RETRYABLE = new Set([Code.InvalidArgument, Code.NotFound, Code.PermissionDenied, Code.Unauthenticated, Code.ResourceExhausted, Code.FailedPrecondition]);

/** 서버 메시지 「이름:숫자」(proto 머리 주석) → 화면 오류. 숫자는 상한 값이다 */
function toSerialError(err: unknown): SerialApiError {
  if (err instanceof SerialApiError) return err; // 응답 모양 검사(bad_response)가 던진 것 — 다시 감싸지 않는다
  if (!(err instanceof ConnectError)) return new SerialApiError('network', true);
  if (err.code === Code.Unauthenticated) return new SerialApiError('login_required', false);
  if (err.code === Code.Unavailable || (err.code === Code.Unknown && !err.rawMessage)) return new SerialApiError('network', true);
  const [name = 'failed', value] = err.rawMessage.split(':');
  const limit = Number(value);
  const detail = name === 'too_short' && Number.isFinite(limit) ? { minChars: limit } : name === 'daily_limit_reached' && Number.isFinite(limit) ? { dailyCap: limit } : {};
  return new SerialApiError(name, !NOT_RETRYABLE.has(err.code), detail);
}

async function call<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    throw toSerialError(err);
  }
}

const AI_FROM_PB: Readonly<Partial<Record<AiUsagePB, AiUsage>>> = {
  [AiUsagePB.NONE]: 'none',
  [AiUsagePB.ASSISTED]: 'assisted',
  [AiUsagePB.GENERATED]: 'generated',
};
const AI_TO_PB: Readonly<Record<string, AiUsagePB>> = { none: AiUsagePB.NONE, assisted: AiUsagePB.ASSISTED, generated: AiUsagePB.GENERATED };

function iso(ts: Timestamp | undefined): string {
  return ts ? timestampDate(ts).toISOString() : '';
}

/** 서버가 모르는 AI 사용 정도를 내려 보내면(새 값) 화면이 그리지 못하므로 응답 모양 오류로 본다 */
function work(w: WorkPB | undefined): Work {
  const aiUsage = w ? AI_FROM_PB[w.aiUsage] : undefined;
  if (!w || !aiUsage) throw new SerialApiError('bad_response', false);
  return { id: w.id, title: w.title, synopsis: w.synopsis, aiUsage, episodeCount: w.episodeCount, createdAt: iso(w.createdAt), updatedAt: iso(w.updatedAt), mine: w.mine };
}

function summary(e: EpisodeSummaryPB | undefined): EpisodeSummary {
  if (!e) throw new SerialApiError('bad_response', false);
  return { id: e.id, no: e.no, title: e.title, charCount: e.charCount, publishedAt: iso(e.publishedAt) };
}

function episode(e: EpisodePB | undefined): Episode {
  if (!e) throw new SerialApiError('bad_response', false);
  return { ...summary(e.summary), workId: e.workId, body: e.body, prevId: e.prevId, nextId: e.nextId, mine: e.mine };
}

/** transport 는 플러그인 진입점이 rpcTransport(apiBase) 로 만든다 — 어댑터는 코어 composition 을 import 하지 않는다(5존 경계). 테스트는 가짜 전송을 넘긴다 */
export function serialApi(transport: Transport) {
  const client = createClient(SerialService, transport);
  return {
    listWorks: (cursor: string): Promise<WorksPage> =>
      call(async () => {
        const res = await client.listWorks({ cursor });
        return { works: res.works.map(w => work(w)), nextCursor: res.nextCursor };
      }),
    getWork: (id: string): Promise<WorkDetail> =>
      call(async () => {
        const res = await client.getWork({ id });
        return { work: work(res.work), episodes: res.episodes.map(e => summary(e)) };
      }),
    getEpisode: (id: string): Promise<Episode> => call(async () => episode((await client.getEpisode({ id })).episode)),
    createWork: (input: { title: string; synopsis: string; aiUsage: string }): Promise<Work> =>
      call(async () => work((await client.createWork({ title: input.title, synopsis: input.synopsis, aiUsage: AI_TO_PB[input.aiUsage] ?? AiUsagePB.UNSPECIFIED })).work)),
    publishEpisode: (workId: string, input: { title: string; body: string }): Promise<EpisodeSummary> =>
      call(async () => summary((await client.publishEpisode({ workId, title: input.title, body: input.body })).episode)),
    /** 내 작품 내리기 — 지우지 않고 숨긴다. 회차도 함께 안 보인다 */
    removeWork: (id: string): Promise<void> => call(async () => void (await client.removeWork({ id }))),
    /** 내 회차 하나 내리기 — 번호는 다시 쓰지 않는다 */
    removeEpisode: (id: string): Promise<void> => call(async () => void (await client.removeEpisode({ id }))),
  };
}

export type SerialApi = ReturnType<typeof serialApi>;
